<script setup lang="ts">
  import { ref, watch } from 'vue';
  import dayjs from 'dayjs';
  import weekday from 'dayjs/plugin/weekday';
  import WButton from 'src/components/base/Button.vue';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import { useRepairFinder, type RepairLocation } from 'src/composables/repairFinder';
  import { themeIcons } from 'src/utils/config';

  dayjs.extend(weekday);

  const props = withDefaults(
    defineProps<{
      location: RepairLocation;
      editable?: boolean;
      hasActions?: boolean;
    }>(),
    {
      editable: true,
      hasActions: true,
    }
  );

  const emit = defineEmits<{
    (e: 'save', location: RepairLocation): void;
    (e: 'fullInfo'): void;
  }>();

  const cardRef = ref<HTMLDivElement | null>(null);
  const { toggleRepairLocationState, setActiveMarker } = useRepairFinder();

  const dayOfWeek = dayjs().weekday();

  function selectCard() {
    const { detail } = toggleRepairLocationState({ index: props.location.id || -1, property: 'is_active' });
    if (detail) {
      setActiveMarker(detail);
    }
  }

  function onSelect() {
    emit('save', props.location);
  }

  watch(
    () => props.location,
    () => {
      if (props.location.is_active === true) {
        cardRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    { deep: true }
  );

  function getSchedule(location: RepairLocation, dayNr: number = dayOfWeek) {
    if (!location.opening_hours || !location.opening_hours.length) return null;
    return location.opening_hours[dayNr];
  }

  function checkIfOpen(location: RepairLocation) {
    const schedule = getSchedule(location);
    if (!schedule || !schedule.open) return false;

    const now = dayjs();
    const fromTime = dayjs(schedule.from, 'HH:mm');
    const toTime = dayjs(schedule.to, 'HH:mm');
    return now.isAfter(fromTime) && now.isBefore(toTime);
  }

  function getFormattedTime(location: RepairLocation) {
    const schedule = getSchedule(location);
    if (!schedule || !schedule.open) return 'Closed Today';

    const now = dayjs();
    const fromTime = dayjs(schedule.from, 'HH:mm');
    const toTime = dayjs(schedule.to, 'HH:mm');

    if (now.isBefore(fromTime)) {
      return `Opens ${schedule.from}`;
    } else if (now.isAfter(toTime)) {
      const tomorrowSchedule = getSchedule(location, dayjs().add(1, 'day').weekday());
      if (tomorrowSchedule?.open) {
        const fromTimeTmrw = dayjs(tomorrowSchedule.from, 'HH:mm').add(1, 'day');
        if (now.isBefore(fromTimeTmrw)) {
          return `Opens ${tomorrowSchedule.from} Tomorrow`;
        }
      }
      return 'Closed';
    }
    return `Closes ${schedule.to}`;
  }
</script>

<template>
  <div
    ref="cardRef"
    :class="[
      { '!border-primary-500 !bg-primary-100': props.editable && props.location.is_active },
      { 'cursor-pointer': props.editable },
      'widget-repair-card w-full rounded-custom border border-secondary-500 bg-transparent transition-all',
    ]"
    @click="selectCard"
  >
    <div class="widget-repair-card_head flex items-center justify-between border-b border-secondary-500 p-4">
      <div class="flex items-center gap-2">
        <div>
          <WidgetIcon :icon="themeIcons.marker" class="h-6 w-6" />
        </div>
        <h4 class="text-lg font-bold">{{ props.location.name }}</h4>
        <WidgetIcon v-if="location?.certified" :icon="themeIcons.certified" class="size-6 text-primary" />
      </div>
      <WButton
        v-if="location?.latitude"
        tag="a"
        target="_blank"
        :href="location.google_link"
        type="ghost"
        class="flex flex-col items-center"
      >
        <div class="rounded-full bg-primary-500 p-2">
          <WidgetIcon :icon="themeIcons.directions" class="text-white" />
        </div>
        <!-- <span class="body1-bold mt-1">Directions</span> -->
      </WButton>
    </div>

    <!-- <div class="flex justify-around border-b border-secondary-500 p-4">
      disabled as we do not want users to call centers
       <WButton
        v-if="props.location.telephone"
        type="ghost"
        tag="a"
        :href="`tel:${props.location.telephone}`"
        class="flex flex-col items-center"
      >
        <div class="rounded-full bg-primary-500 p-3">
          <WidgetIcon :icon="themeIcons.phone" class="text-white" />
        </div>
        <span class="body1-bold mt-1">Call collision center</span>
      </WButton>
    </div> -->

    <div class="widget-repair-card_body body1 flex flex-col gap-2 p-4 text-fontPrimary">
      <!-- <div class="flex"> hidden we do not want users calling repair locations
        <span class="min-w-[5rem] font-bold">Phone:</span>
        <span>{{ props.location.telephone }}</span>
      </div> -->
      <div class="flex">
        <span class="min-w-[5rem] font-bold">Address:</span>
        <span>{{ props.location.address?.as_string }}</span>
      </div>
      <div class="flex">
        <span class="min-w-[5rem] font-bold">Hours:</span>
        <span>
          <span
            :class="{
              'text-positive-500': checkIfOpen(props.location),
              'text-negative-500': !checkIfOpen(props.location),
            }"
          >
            {{ checkIfOpen(props.location) ? 'Open' : 'Closed' }}
          </span>
          – {{ getFormattedTime(props.location) }}
        </span>
      </div>
      <div class="flex" v-if="location?.business?.website">
        <span class="min-w-[5rem] font-bold">Links:</span>
        <div>
          <a
            v-if="location?.business?.website"
            :href="location.business?.website"
            class="body1-bold text-primary underline"
            target="_blank"
            >Website</a
          >
          <!-- <a
            v-if="location?.business"
            :href="location.business?.website"
            class="body1-bold ml-2 text-primary underline"
            target="_blank"
            >Details</a
          > -->
        </div>
      </div>
      <div>
        <WButton
          v-if="props.editable && !location.selected"
          @click.stop="onSelect"
          class="mt-4 rounded bg-primary-500 px-8 py-2 text-white"
        >
          Select
        </WButton>
      </div>
    </div>
  </div>
</template>
